import { getTokenDollarValue, tokenValuesQueryOptions } from "@/api/data";
import { myWalletQueryOptions } from "@/api/me";
import { CompareAtTokenIcon, TokenIcon } from "@/icons/givenwell";
import { generateSrcSet } from "@/utils/images";
import { config } from "@common/utils/config";
import { formatWholeNumber, locale } from "@common/utils/i18n";
import { Box, Flex, FlexProps, Skeleton, Span, Text } from "@givenwell/components";
import {
  CalendarIcon,
  CompassNavigatorIcon,
  LocationPinIcon,
  ScreenIcon,
  UserProtectionIcon,
} from "@givenwell/core-solid-icons";
import { GetHomeControllerListingsResponseListing, SearchResult } from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { Link, linkOptions } from "@tanstack/react-router";
import { useMemo } from "react";
import { Tooltip } from "./ui/Tooltip";

type ListingCardProps = {
  listing: SearchResult | GetHomeControllerListingsResponseListing;
};
export function ListingCard({ listing }: ListingCardProps) {
  const walletQuery = useQuery(myWalletQueryOptions);
  const tokenValuesQuery = useQuery(tokenValuesQueryOptions);

  const tokenCurrency = walletQuery.data?.token_currency;
  const tokenValues = tokenValuesQuery.data;

  const allPrices = listing.prices;
  const nonZeroMinPrice = useMemo(() => Math.min(...allPrices.filter(p => p > 0)), [allPrices]);

  const formatPrice = (amount: number) => {
    if (tokenCurrency) {
      const result = getTokenDollarValue(amount, tokenCurrency, tokenValues);
      if (result) {
        return result.dollarValue.toLocaleString(undefined, {
          minimumFractionDigits: result.decimalPlaces,
          maximumFractionDigits: result.decimalPlaces,
        });
      }
    }
    return formatWholeNumber(amount);
  };

  let priceText = formatPrice(nonZeroMinPrice || listing.max_price);
  if (nonZeroMinPrice !== listing.max_price) {
    priceText += `+`;
  }
  if (listing.min_price === 0 && listing.max_price === 0) {
    priceText = "Free";
  }

  const compareAtText =
    listing.compare_at_price && listing.compare_at_price > nonZeroMinPrice
      ? formatPrice(listing.compare_at_price)
      : null;

  const isEAP = listing.eap_available;
  const isInPerson = listing.location_types.includes("in_person");
  const isGetaway = listing.location_types.includes("getaway");

  const nextAvailable = "next_available" in listing ? listing.next_available : undefined;
  const freeToTry = listing.min_price === 0 && priceText !== "Free";

  const showLocation = listing.location_types.length > 0 && !config.features.onlineListingsOnly;

  const DescriptionIcon = isGetaway ? CompassNavigatorIcon : isInPerson ? LocationPinIcon : ScreenIcon;

  const locationText =
    "location_address" in listing && listing.location_address
      ? `${listing.location_address.suburb ? listing.location_address.suburb + ", " : ""}${listing.location_address.city}`
      : isInPerson || isGetaway
        ? "In-Person"
        : "Online";

  const linkProps = linkOptions({
    to: isEAP ? "/eap/listings/$listingId" : "/marketplace/listings/$listingId",
    params: { listingId: listing.listing_id },
  });

  return (
    <ListingCardRoot>
      <ImageContainer isEAP={isEAP}>
        <Image
          src={listing?.media_url || undefined}
          srcSet={listing?.media_url ? generateSrcSet(listing.media_url) : undefined}
          sizes={"auto, (min-width: 600px) 200px, 50vw"}
          alt={listing?.title}
          loading="lazy"
        />
        {!isEAP && (
          <PriceTag>
            <Box css={{ padding: 2 }}>
              <TokenIcon />
            </Box>
            <Text
              css={{
                fontScale: "sm",
                weight: 500,
                fontFamily: fonts.ftHabit,
                lineHeight: 1,
              }}
            >
              {priceText}
            </Text>
            {compareAtText && (
              <Flex
                css={{
                  items: "center",
                  ml: 4,
                  gap: 2,
                  position: "relative",
                }}
              >
                <CompareAtTokenIcon />
                <Text
                  css={{
                    fontScale: "xs",
                    weight: 300,
                    fontFamily: fonts.ftHabit,
                    color: colors.text3,
                  }}
                >
                  {compareAtText}
                </Text>
                <FauxLineThrough />
              </Flex>
            )}
          </PriceTag>
        )}
      </ImageContainer>
      <Flex>
        <Box css={{ flex: "1 0 0px" }}>
          <Title>
            <Link {...linkProps}>
              {addWordBreaks(listing.title)}
              {/* Cheeky trick to make the link hitbox cover the whole card while keeping the link contents sensible for screen readers / SEO */}
              {/* Does make the text unselectable tho :/ */}
              <Span
                css={{
                  pos: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              />
            </Link>
          </Title>
          <Overline>{listing.eap_available && "byline" in listing ? listing.byline : listing.supplier_name}</Overline>
        </Box>
        {listing.registration_id && (
          <Span css={{ zIndex: 1, ml: 2, size: 16, display: "inline-block", position: "relative" }}>
            <Tooltip
              content={`${listing.eap_available ? listing.title : listing.supplier_name} is a registered practitioner`}
              side="top"
            >
              <UserProtectionIcon
                style={{
                  display: "inline",
                  marginLeft: "auto",
                  width: 14,
                  height: 14,
                  verticalAlign: "middle",
                  color: colors.text2,
                }}
              />
            </Tooltip>
          </Span>
        )}
      </Flex>
      {(showLocation || nextAvailable || freeToTry) && (
        <Flex css={{ mt: 4, gap: 4, items: "flex-start", color: colors.text2, fontSize: 11, lineHeight: "14px" }}>
          {(showLocation || nextAvailable) && (
            <>
              <Box css={{ py: 1 }}>
                {showLocation ? (
                  <DescriptionIcon width="12" height="12" color={colors.text2} />
                ) : nextAvailable ? (
                  <CalendarIcon width="12" height="12" color={colors.text2} />
                ) : null}
              </Box>
              <span>
                {showLocation && locationText}
                {showLocation && nextAvailable && <> &bull; </>}
                {nextAvailable && getBookingTimeString(nextAvailable)}
              </span>
            </>
          )}
          {freeToTry && (
            <span style={{ marginLeft: showLocation || nextAvailable ? 4 : 0 }}>
              <FreeToTryBadge />
            </span>
          )}
        </Flex>
      )}
    </ListingCardRoot>
  );
}

function FreeToTryBadge() {
  return (
    <Span
      css={{
        bottom: 6,
        left: 6,
        rounded: 9999,
        backgroundColor: colors.warmGrey,
        color: colors.text2,
        items: "center",
        px: 5,
        py: 1,
        display: "inline-block",
        textTransform: "uppercase",
        fontSize: 9,
        weight: 400,
      }}
    >
      Try Free
    </Span>
  );
}

function addWordBreaks(str: string) {
  return str.split("/").join("/\u200B");
}

const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(tomorrow.getDate() + 1);

function getBookingTimeString(nextAvailable: string) {
  const date = new Date(nextAvailable);

  function getDateString() {
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    } else if (date.getFullYear() === today.getFullYear()) {
      return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(date);
    } else {
      return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(date);
    }
  }

  return `Available ${getDateString()}`;
}

type ListingCardSkeletonProps = FlexProps;
export function ListingCardSkeleton({ ...props }: ListingCardSkeletonProps) {
  return (
    <Flex
      {...props}
      css={{
        flexDirection: "column",
        ...props.css,
      }}
    >
      <Box css={{ pos: "relative", w: "100%", aspectRatio: 16 / 9, rounded: 16 }}>
        <Skeleton css={{ size: "100%", rounded: "inherit" }} />
      </Box>

      <Box
        css={{
          w: "100%",
          pt: 6,
          flex: "1 0 0px",
        }}
      >
        <Skeleton css={{ w: "50%", h: 17 }} />
        <Skeleton css={{ w: "100%", h: 17, mt: 4 }} />
      </Box>
    </Flex>
  );
}

const FauxLineThrough = styled("div", {
  position: "absolute",
  top: "50%",
  left: -2,
  right: -2,
  height: 1,
  backgroundColor: "#bbbbB4", // Equivalent of text3 on a cream background, but without transparency.
  transform: "translateY(-50%)",
});

const ListingCardRoot = styled("div", {
  flexDirection: "column",
  position: "relative",

  // overflow: "hidden",
});

const ImageContainer = styled("div", {
  pos: "relative",
  w: "100%",
  rounded: 16,
  mb: 6,

  // boxShadow: "0 0 0 1px rgba(0,0,0,.01),0 3px 3px rgba(0,0,0,.01),0 14px 14px rgba(0,0,0,.03)",

  boxShadow:
    "0 1px 1px rgba(0,0,0,.01), 0 2px 2px rgba(0,0,0,.01), 0 8px 8px rgba(0,0,0,.03), 0 14px 14px rgba(0,0,0,.03)",

  variants: {
    isEAP: {
      true: { aspectRatio: 1 },
      false: { aspectRatio: 16 / 9 },
    },
  },
});

const Image = styled("img", {
  objectFit: "cover",
  objectPosition: "center",
  w: "100%",
  h: "100%",
  pos: "absolute",
  top: 0,
  left: 0,
  rounded: "inherit",
});

const PriceTag = styled("span", {
  pos: "absolute",
  display: "flex",
  alignItems: "center",
  bottom: 6,
  left: 6,
  rounded: 9999,
  backgroundColor: colors.cream,
  color: colors.navy,
  items: "center",
  gap: 2,
  pr: 6,
  boxShadow: "0px 0px 2px 1px rgba(0, 0, 0, 0.07)",
});

const Overline = styled("div", {
  fontSize: 12,
  weight: 500,
  color: colors.text2,
  w: "auto",
  lineHeight: "16px",
  lineClamp: 1,
});

const Title = styled("h3", {
  fontScale: "sm",
  lineClamp: 2,
  weight: 500,
  lineHeight: "18px",
  fontFamily: fonts.ftHabit,

  overflowWrap: "break-word",
  wordWrap: "break-word",
  wordBreak: "break-word",
  hyphens: "auto",
});

type RegistrationBadgeProps = { size?: number; margin?: number };
export function RegistrationBadge({ size, margin, ...props }: RegistrationBadgeProps) {
  return (
    <UserProtectionIcon
      {...props}
      style={{ display: "inline", marginLeft: margin, width: size || 18, height: size || 18, verticalAlign: "middle" }}
    />
  );
}
